import { nextTick, onBeforeUnmount, onMounted, watch, type Ref } from "vue";

// Доступность модального окна в одном месте: ловушка фокуса (Tab/Shift+Tab
// по кругу внутри панели), Esc закрывает (если окно можно закрыть), фокус при
// открытии - на первый интерактивный элемент, при закрытии - обратно туда,
// откуда окно открыли; прокрутка страницы на время блокируется.
// active - для окон, которые смонтированы всегда и открываются флагом
// (AppModal); без него окно считается открытым всё время, пока смонтировано
// (диалоги под v-if).
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useDialogA11y(
  panel: Ref<HTMLElement | null>,
  opts: {
    onClose: () => void;
    dismissible?: () => boolean;
    active?: Ref<boolean>;
    // Под-окно (экран успеха, кадрирование), нарисованное рядом с панелью:
    // пока оно открыто, фокус ловится внутри него, а не в панели под ним.
    activeRoot?: () => HTMLElement | null;
  }
) {
  const titleId = `dialog-title-${Math.random().toString(36).slice(2)}`;
  let opener: HTMLElement | null = null;

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (opts.dismissible && !opts.dismissible()) return;
      e.stopPropagation();
      opts.onClose();
      return;
    }
    const root = opts.activeRoot?.() ?? panel.value;
    if (e.key !== "Tab" || !root) return;
    // Вложенное окно обрабатывает Tab само - внешнее не должно перехватить.
    e.stopPropagation();
    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const open = async () => {
    opener = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    await nextTick();
    (panel.value?.querySelector<HTMLElement>(FOCUSABLE) ?? panel.value)?.focus();
  };

  const close = () => {
    document.body.style.overflow = "";
    opener?.focus?.();
    opener = null;
  };

  // Открылось под-окно - переносим фокус внутрь него.
  if (opts.activeRoot) {
    watch(opts.activeRoot, async (root) => {
      if (!root) return;
      await nextTick();
      root.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }, { flush: "post" });
  }

  if (opts.active) {
    watch(opts.active, (v) => (v ? void open() : close()), { immediate: true });
  } else {
    onMounted(() => void open());
  }
  onBeforeUnmount(close);

  return { titleId, onKeydown };
}
